import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.contractor_type === 'sales') {
        navigate('/sales');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Authentication failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Side - Branding */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/32743799/pexels-photo-32743799.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-slate-900/70" />
        
        {/* Content */}
        <div className="relative z-10 p-12 flex flex-col justify-between h-full w-full">
          <div className="flex items-center gap-3">
            <span className="font-heading font-extrabold text-2xl text-white tracking-tight">BRICK</span>
          </div>
          
          <div>
            <h1 className="font-heading text-5xl md:text-6xl font-bold tracking-tight text-white mb-4">
              Timesheet<br />
              <span className="text-brick-cyan">Portal</span>
            </h1>
            <p className="text-slate-300 text-lg max-w-md leading-relaxed">
              Track your hours, submit timesheets, and manage bi-weekly reporting periods.
            </p>
          </div>

          <p className="text-xs text-slate-400 uppercase tracking-widest">
            Internal Tool — Contractors Only
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <span className="font-heading font-extrabold text-2xl text-slate-900 tracking-tight">BRICK</span>
          </div>

          <div className="mb-8">
            <h2 className="font-heading text-3xl font-bold tracking-tight text-slate-900 mb-2">
              Sign In
            </h2>
            <p className="text-slate-500">
              Enter your credentials to access your timesheet
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Email Address
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                data-testid="email-input"
                className="h-12 bg-white border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="password-input"
                className="h-12 bg-white border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-12 bg-brick-red hover:bg-[#c4151d] font-medium"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : 'Sign In'}
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-500">
            Contact your administrator for account access
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
